import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { merge, of } from 'rxjs';
import { switchMap, withLatestFrom, filter, map, catchError, distinctUntilChanged } from 'rxjs/operators';
import { setActiveCompany, setTabYear, setCompanyCode, setCompanyName, setCompanyCountry, loadInactivePeriods, loadInactivePeriodsSuccess, loadInactivePeriodsFailure } from '../actions/company-form.actions';
import { selectActiveCompanyId } from '../selectors/company-form.selectors';
import { CompanyDetailsService } from '../../services/companies/company-details.service';
import { CompanyInactivePeriodService } from '../../services/companies/company-inactive-period.service';
import { Helpers } from '../../utilities/helpers';

@Injectable()
export class CompanyFormEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private companyDetailsService = inject(CompanyDetailsService);
  private companyInactivePeriodService = inject(CompanyInactivePeriodService);

  /**
   * Load the company summary (name/code/country shown in the header) whenever the
   * active company changes or any tab's year changes.
   *
   * On first load both `setActiveCompany` (for the default measurement year) and
   * the eagerly-rendered details tab's initial `setTabYear` fire with the same
   * year, which previously issued two identical summary requests. Merging both
   * triggers into one stream lets `distinctUntilChanged` on (companyId, year)
   * collapse that duplicate. `switchMap` still cancels in-flight requests on rapid
   * year changes.
   */
  loadCompanySummary$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(setActiveCompany),
        map(({ companyId }) => ({ companyId, year: Number(Helpers.measurements()[0]) }))
      ),
      this.actions$.pipe(
        ofType(setTabYear),
        withLatestFrom(this.store.select(selectActiveCompanyId)),
        filter(([_, companyId]) => !!companyId),
        map(([{ year }, companyId]) => ({ companyId: companyId!, year: Number(year) }))
      )
    ).pipe(
      distinctUntilChanged((a, b) => a.companyId === b.companyId && a.year === b.year),
      switchMap(({ companyId, year }) =>
        this.companyDetailsService.getSummary(companyId, year).pipe(
          map(summary => ({ summary })),
          catchError(() => of(null))
        )
      ),
      filter(result => result !== null),
      switchMap(({ summary }) => [
        setCompanyCode({ companyCode: summary.code ?? null }),
        setCompanyName({ companyName: summary.companyName }),
        setCompanyCountry({ companyCountry: summary.countryName ?? null })
      ])
    )
  );

  /**
   * On every company activation, load that company's inactive periods so any
   * tab can derive whether the selected year falls inside an inactive window.
   */
  loadInactivePeriodsOnActivation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveCompany),
      map(({ companyId }) => loadInactivePeriods({ companyId }))
    )
  );

  loadInactivePeriods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadInactivePeriods),
      switchMap(({ companyId }) =>
        this.companyInactivePeriodService.getAll(companyId).pipe(
          map(inactivePeriods => loadInactivePeriodsSuccess({ inactivePeriods })),
          catchError(err => of(loadInactivePeriodsFailure({ error: String(err) })))
        )
      )
    )
  );

}
